import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  Grid,
  Inscription,
  ListItemField,
  RadioButton,
} from '@common-fe/common-fe';

const CardItem = ({ testId, title, value, node, style }: ListItemField & { testId?: string }) => {
  return (
    <Box data-tesId={`${testId}_cell`}>
      <Inscription
        data-tesId={`${testId}_title`}
        size="small"
        color="textSecondary"
        weight={500}
        lineHeight="18px"
      >
        {title}
      </Inscription>
      <Inscription
        data-tesId={`${testId}_value`}
        title={value}
        color="textBody"
        lineHeight="20px"
        style={{ 
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...style,
        }}
      >
        {value || node}
      </Inscription>
    </Box>
  );
};

const FULL_WIDTH_FIELD_KEYS = ['address'];

interface Props {
  testId?: string;
  itemKey?: string;
  itemFields?: ListItemField[];
  isSelected?: boolean;
  onSelect?: (value?: string) => void;
  children?: React.ReactNode;
  isEditMode?: boolean;
}

const RadioButtonCard: React.FC<Props> = ({
  testId, itemKey, isSelected, onSelect, itemFields, isEditMode, children,
}) => {
  const [isExpanded, setIsExpanded] = useState(!isEditMode);

  const gridFields = useMemo(
    () => (itemFields || []).filter((field) => !FULL_WIDTH_FIELD_KEYS.includes(field.key ?? '')),
    [itemFields]
  );
  const fullWidthFields = useMemo(
    () => (itemFields || []).filter((field) => FULL_WIDTH_FIELD_KEYS.includes(field.key ?? '')),
    [itemFields]
  );

  const slicedGridFields = useMemo(() => gridFields.slice(0, 3), [gridFields]);
  const visibleGridFields = useMemo(
    () => (isExpanded ? gridFields : slicedGridFields),
    [isExpanded, gridFields, slicedGridFields]
  );

  const gridColumns = useMemo(
    () => Array.from({ length: Math.min(Math.max(visibleGridFields.length, 1), 3) }, () => '1fr'),
    [visibleGridFields.length]
  );

  const handleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleSelect = useCallback(() => {
    if (onSelect && itemKey) {
      onSelect(itemKey);
      setIsExpanded(true);
    }
  }, [itemKey, onSelect]);

  return (
    <Box
      data-testid={`${testId}_radio-button-card`}
      background="canvas"
      round="container1Round"
      pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
      border={{ size: '1px', color: 'border2' }}
    >
      <Box direction="row">
        {isEditMode && (
          <Box direction="row" margin={{ vertical: 'spacing8', right: 'spacing12' }}>
            <RadioButton
              data-testId={`${testId}_radio-button`}
              name={`${testId}_radio-button`}
              checked={isSelected}
              onChange={handleSelect}
            />

            <Box
              width={{ min: 'spacing24' }}
              height={{ min: 'spacing24' }}
              margin={{ left: 'spacing12' }}
              onClick={handleExpand}
            >
              {isExpanded ? (
                <ChevronUpIcon size="spacing24" color="iconPrimary" />
              ) : (
                <ChevronDownIcon size="spacing24" color="iconPrimary" />
              )}
            </Box>
          </Box>
        )}

        <Box direction="column" width="100%">
          <Grid
            width="100%"
            columns={gridColumns}
            gap={{
              column: 'spacing12',
              row: 'spacing8'
            }}
          >
            {visibleGridFields?.map((cardItem) => (
              <CardItem
                testId={`${testId}_${cardItem.key}`}
                key={cardItem.key}
                type={cardItem.type}
                title={cardItem.title}
                value={cardItem.value}
                node={cardItem?.node}
                style={cardItem?.style}
              />
            ))}
          </Grid>

          {isExpanded && fullWidthFields.map((cardItem) => (
            <Box key={cardItem.key} margin={{ top: 'spacing8' }}>
              <CardItem
                testId={`${testId}_${cardItem.key}`}
                type={cardItem.type}
                title={cardItem.title}
                value={cardItem.value}
                node={cardItem?.node}
                style={cardItem?.style}
              />
            </Box>
          ))}
        </Box>
      </Box>
      {children}
    </Box>
  );
};

export default RadioButtonCard;
